"use client";
import type { JSX } from "react";
import { useState } from "react";
import { motion, type Variants } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CTA_GRADIENT_BACKGROUND_CLASSNAME } from "@/lib/cta-styles";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const faqItems = [
  {
    question: "What services does MuteTaxes provide?",
    answer:
      "MuteTaxes helps with company formation, company secretary support, bookkeeping, accounting, tax filing, ongoing compliance, and business banking guidance.",
  },
  {
    question: "Do you help both new and existing companies?",
    answer:
      "Yes. We support business owners who are starting a new company and those who already have a company but need help managing records, filings, tax, or compliance.",
  },
  {
    question: "Can foreigners set up a company with MuteTaxes?",
    answer:
      "Yes. We help foreign founders and international business owners understand the setup process, required documents, and next steps for company formation.",
  },
  {
    question: "What do I need to get started?",
    answer:
      "We usually start by understanding your business type, company stage, ownership details, and the support you need. After that, we guide you on the required documents and next steps.",
  },
  {
    question: "How long does company formation take?",
    answer:
      "Timelines depend on the company type, documents, and jurisdiction requirements. Once we understand your situation, we can guide you on the expected process and timing.",
  },
  {
    question: "Do you provide bookkeeping and accounting?",
    answer:
      "Yes. We help keep transactions, invoices, receipts, monthly records, and financial reports organized so your business has a clear financial foundation.",
  },
  {
    question: "Do you help with tax filing?",
    answer:
      "Yes. We help prepare your records and support the filing process so your company stays on top of its tax responsibilities.",
  },
  {
    question: "What is company secretary support?",
    answer:
      "Company secretary support helps keep statutory records, annual returns, registered address matters, and important company obligations properly managed.",
  },
  {
    question:
      "Can I switch to MuteTaxes if I already have an accountant or company secretary?",
    answer:
      "Yes. We can review your current setup, understand what is missing or pending, and help move your ongoing company support into a clearer process.",
  },
  {
    question: "Can you help if my records are messy or incomplete?",
    answer:
      "Yes. We can review what you already have, identify what is missing, and help organize your records so your company is easier to manage moving forward.",
  },
  {
    question: "Do you help with business banking?",
    answer:
      "Yes. We provide guidance for business banking and payment setup where applicable, based on your company structure, documents, and provider requirements.",
  },
  {
    question: "What types of businesses do you work with?",
    answer:
      "We work with E-commerce businesses, service companies, construction firms, trading companies, digital brands, and other business types that need structured financial and compliance support.",
  },
  {
    question: "Is MuteTaxes only for online businesses?",
    answer:
      "No. MuteTaxes is not only for online businesses. We support both traditional and digital companies that need help with records, reports, tax, and compliance.",
  },
  {
    question: "What happens after I book a call?",
    answer:
      "We learn about your situation, understand what support you need, and guide you on the next steps, documents, and service options.",
  },
  {
    question: "How do I get started?",
    answer:
      "You can talk to a specialist for quick answers or book a consultation call at a time that suits your schedule.",
  },
];

const INITIAL_VISIBLE_COUNT = 8;

export const GlobalBusinessSupportMapSection = (): JSX.Element => {
  const [showAll, setShowAll] = useState(false);
  const visibleFaqItems = showAll
    ? faqItems
    : faqItems.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <section
      aria-labelledby="faq-heading"
      className="relative flex w-full flex-col max-sm:mt-20 items-center gap-14 px-4 pt-6 sm:px-6"
    >
      <header className="flex w-full flex-col items-center gap-6">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100">
          Have questions?
        </Badge>
        <motion.h2
          id="faq-heading"
          className="[font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.15] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Find quick answers below.
        </motion.h2>
      </header>
      <div className="flex w-full max-w-[800px] flex-col">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col gap-[13px]"
        >
          {visibleFaqItems.map(({ question, answer }, index) => (
            <AccordionItem
              key={question}
              value={`faq-${index + 1}`}
              className="relative overflow-hidden rounded-[20px] border border-[#ffffff29] bg-[#0f1218]"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-[138px] left-[calc(50%+240px)] h-[171px] w-[171px] rounded-full bg-[#6fa6ff] blur-[71.73px]"
              />
              <AccordionTrigger className="relative z-10 flex min-h-[80px] w-full items-center justify-between gap-4 px-6 max-sm:py-3 py-6 text-left no-underline hover:no-underline [&>svg]:h-8 [&>svg]:w-8 [&>svg]:shrink-0 [&>svg]:text-white">
                <span className="[font-family:'Suisse_Intl_Trial-Medium',Helvetica] max-sm:text-base text-xl font-medium leading-7 tracking-[0] text-white">
                  {question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="relative z-10 px-6 pb-6 pt-0 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] max-sm:text-sm text-base font-normal leading-6 text-[#ffffffb2]">
                {answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {faqItems.length > INITIAL_VISIBLE_COUNT && (
          <div className="flex justify-center pt-14">
            <Button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className={`h-auto gap-2 rounded-3xl px-6 py-4 font-inter text-sm font-medium leading-4 text-white ${CTA_GRADIENT_BACKGROUND_CLASSNAME}`}
            >
              {showAll ? "Show Less" : "Show More"}
              <img
                className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
                alt=""
                aria-hidden="true"
                src="/home-v3/component-1-3.svg"
              />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
